import { useEffect, useState } from "react";

import { CircleCheckBig, CircleX, ClipboardIcon } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  clearAllStatesses,
  createInvoice,
  getInvoice,
  getInvoiceQr,
} from "../../../../Redux/CryptoChill/CryptoChillAction";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "Components/ui/dialog";
import { Input } from "Components/ui/input";
import { Skeleton } from "Components/ui/skeleton";
import useCountdown from "hooks/use-countDown";
import { io } from "socket.io-client";
import { toast } from "sonner";
import { getChallengeRules } from "../../../../Redux/ChallengeRules/ChallengeRulesActions";
import { useAuth } from "@clerk/clerk-react";
import trailDots from "../../../../Assets/Images/trailDots.svg";
import { Separator } from "Components/ui/separator";
import { Button } from "Components/ui/button";

function CryptoChillPopup({
  open,
  setOpen,
  step,
  setStep,
  totalAmount,
  selectedCurrency,
  id,
  type,
}) {
  const {
    profile,
    rates,
    createInvoiceSuccess,
    invoice,
    qrCode,
    createInvoiceLoading,
    getRatesLoading,
    getQRCodeLoading,
    getInvoiceLoading,
    getProfileLoading,
  } = useSelector((state) => state.InvoiceReducer);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [kind, setKind] = useState("");
  const [challengeType, setChallengeType] = useState();
  const [selectedPlatform, setSelectedPlatform] = useState();
  const [accountBalance, setAccountBalance] = useState();
  const [accountType, setAccountType] = useState();
  const { formattedTime, progress, restart } = useCountdown(
    invoice?.result?.expires_at
  );

  const { getToken } = useAuth();
  const handleCrypto = (currenci) => {
    setKind(currenci?.kind);
    const passthroughData = { challengeId: id, type: type };
    dispatch(
      createInvoice(getToken, {
        amount: totalAmount?.toString(),
        currency: selectedCurrency,
        kind: currenci?.kind,
        profile_id: process.env.REACT_APP_CRYPTO_ADDON,
        passthrough: JSON.stringify(passthroughData),
      })
    );
  };

  const {
    addons,
    addonsSuccess,
    addonsError,
    addonsLoading,
    payablePayment,
    payablePaymentError,
    payablePaymentSuccess,
    payablePaymentLoading,
    createDraftLoading,
  } = useSelector((state) => state.ChallengeSetupReducer);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCloseDialog = () => {
    if (paymentStatus === "complete") {
      // Navigate to home page
      setOpen(false); // Close the dialog
      if (id) dispatch(getChallengeRules(getToken, id));
    }
    setOpen(false); // Close the dialog
  };

  useEffect(() => {
    if (createInvoiceSuccess) {
      dispatch(getInvoice(getToken, createInvoiceSuccess?.result?.id));
      dispatch(getInvoiceQr(getToken, createInvoiceSuccess?.result?.id));
      setStep(2);
      SocketIo.emit("joinInvoiceRoom", {
        invoiceId: createInvoiceSuccess?.result?.id,
      });
      SocketIo.on("addon-invoice-update", (data) => {
        setPaymentStatus(data.status);
      });
    }
  }, [createInvoiceSuccess]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setPaymentStatus("");
      restart();
      dispatch(clearAllStatesses());
    }
  }, [open]);

  const [openFinal, setOpenFinal] = useState(false);

  useEffect(() => {
    if (paymentStatus) {
      setOpenFinal(true);
      setOpen(false);
    }
  }, [paymentStatus]);

  const paramId = useParams().id;

  return (
    <>
      <Dialog open={open} onOpenChange={handleCloseDialog}>
        <DialogContent className="md:max-w-[55%] max-w-[90%]  overflow-x-hidden shadow-xl">
          <DialogTitle className="text-2xl font-G_Bold">
            Finalize your payment{" "}
            <span className="text-sm text-primary ml-4">
              {paymentStatus &&
                "Your Payment is: " + paymentStatus.toUpperCase()}
            </span>
          </DialogTitle>
          {(getProfileLoading ||
            getRatesLoading ||
            getQRCodeLoading ||
            getInvoiceLoading ||
            createInvoiceLoading ||
            createDraftLoading) &&
          paymentStatus !== "complete" &&
          open ? (
            <>
              <Skeleton className="bg-primary-opacity p-6 rounded-xl w-full" />
              <Skeleton className="bg-primary-opacity p-6 rounded-xl w-full" />
            </>
          ) : (
            profile &&
            profile?.result?.currencies?.length > 0 &&
            step == 1 &&
            profile?.result?.currencies.map((currenci) => {
              const rate = rates?.result?.find(
                (rate) => rate?.symbol === currenci?.kind
              )?.rate_usd;
              const totalAmountInCurrency = rate ? totalAmount / rate : 0;
              return (
                <div
                  className="flex items-center justify-between p-4 border-2 border-borderChallengeOpacity rounded-xl cursor-pointer"
                  onClick={() => handleCrypto(currenci)}
                >
                  <span className="text-white text-sm">
                    Pay with {currenci?.kind}
                  </span>
                  <span className="text-white text-sm">
                    {totalAmountInCurrency?.toFixed(6)}
                  </span>
                </div>
              );
            })
          )}
          {step == 2 &&
          (getInvoiceLoading ||
            createInvoiceLoading ||
            getQRCodeLoading ||
            createDraftLoading) &&
          paymentStatus !== "complete" &&
          progress < 100 ? (
            <div className="flex flex-col gap-4">
              <span className="text-white-opacity text-sm">
                To complete the payment, please open your crypto wallet and
                transfer the specified amount to the address listed here.
              </span>
              <div className="w-full flex flex-row items-center gap-6">
                <div className="w-[70%] flex flex-col gap-4">
                  <Skeleton className="bg-primary-opacity p-4 rounded-xl w-full" />
                  <Skeleton className="bg-primary-opacity p-4 rounded-xl w-full" />
                  <Skeleton className="bg-primary-opacity p-4 rounded-xl w-full" />
                </div>
                <div className="w-[30%] flex flex-col gap-4">
                  <Skeleton className="bg-primary-opacity h-[150px] rounded-xl w-full" />
                </div>
              </div>
            </div>
          ) : step == 2 && paymentStatus !== "complete" && progress < 100 ? (
            <div className="flex flex-col gap-4">
              <span className="text-white-opacity text-sm">
                To complete the payment, please open your crypto wallet and
                transfer the specified amount to the address listed here.
              </span>
              <div className="w-full flex flex-row items-center gap-6">
                <div className="w-[70%] flex flex-col gap-4">
                  <div className="flex flex-col gap-1 ">
                    <label className="text-white text-sm">
                      {kind} payment address
                    </label>
                    <div className="relative">
                      <Input
                        className="pr-12"
                        type="text"
                        value={invoice?.result?.address}
                        disabled
                      />
                      <div className="absolute top-1/2 right-4 transform -translate-y-1/2 cursor-pointer">
                        <ClipboardIcon
                          className="h-4 w-4 text-white"
                          color="var(--primary)"
                          onClick={() => {
                            navigator.clipboard
                              .writeText(invoice?.result?.address)
                              .then(() => {
                                toast.success("Address Copied to clipboard");
                              })
                              .catch((err) => {
                                console.error("Failed to copy address: ", err);
                              });
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-white text-sm">
                      Amount to sent in {kind}
                    </label>
                    <Input
                      type="text"
                      value={invoice?.result?.amount?.invoiced?.amount}
                      disabled
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-row items-center justify-between">
                      <label className="text-white text-sm">
                        Expires in {formattedTime}
                      </label>
                      <span
                        className="text-primary font-G_Regular text-sm cursor-pointer"
                        onClick={() => {
                          dispatch(
                            createInvoice(getToken, {
                              amount:
                                invoice?.result?.amount?.requested?.amount,
                              currency:
                                invoice?.result?.amount?.requested?.currency,
                              kind: invoice?.result?.amount?.invoiced?.currency,
                              profile_id: invoice?.result?.profile_id,
                              passthrough: invoice?.result?.passthrough,
                            })
                          );
                        }}
                      >
                        Renew
                      </span>
                    </div>
                    <div className="mt-2 relative">
                      <div className="w-full h-2 !bg-secondary rounded-full">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-[30%] flex flex-col justify-center items-center gap-4">
                  {" "}
                  <span className="text-white text-sm">Scan QR code</span>
                  <div
                    className="qr-code bg-white p-0"
                    dangerouslySetInnerHTML={{ __html: qrCode }}
                  />
                </div>
              </div>
            </div>
          ) : paymentStatus === "complete" ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <CircleCheckBig
                color="#00ff2a"
                strokeWidth={1.5}
                width={60}
                height={60}
              />
              <div className="text-white text-lg font-G_SemiBold">
                Payment successful
              </div>
            </div>
          ) : progress == 100 && paymentStatus !== "complete" ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <CircleX
                color="#ff0000"
                strokeWidth={1.5}
                width={60}
                height={60}
              />
              <div className="text-white text-lg font-G_SemiBold">
                Payment expired
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openFinal}
        onOpenChange={() => {
          setOpenFinal(true);
        }}
      >
        <DialogContent
          className=" overflow-x-hidden shadow-xl
              "
        >
          <DialogHeader className="font-G_SemiBold text-2xl">
            Payment Status
          </DialogHeader>
          <Separator className="bg-borderChallenge w-[120%] ml-[-10%] opacity-20 h-[0.2rem] rounded-xl" />
          <DialogDescription className={`flex flex-col `}>
            <div className="flex flex-col items-start">
              <div className="flex flex-row gap-4 items-center">
                <div className="w-6 h-6 rounded-full aspect-square bg-primary"></div>
                <div className="">
                  <div className="font-G_Bold text-lg">Payment Pending</div>
                  <div className="opacity-[0.44] font-G_Regular text-base">
                    Your Payment has been received.
                  </div>
                </div>
              </div>
              {
                <img
                  src={trailDots}
                  alt={"trails"}
                  className={`ml-[0.625rem] -mt-2 -mb-2 ${
                    paymentStatus == "pending" && "opacity-[0.43]"
                  }`}
                />
              }
            </div>
            <div className="flex flex-col items-start">
              <div
                className={`${
                  paymentStatus !== "confirmed" && paymentStatus !== "complete"
                    ? "opacity-[0.43]"
                    : "opacity-[1]"
                } flex flex-row gap-4 items-center`}
              >
                <div className="w-6 h-6 rounded-full aspect-square bg-primary"></div>
                <div>
                  <div className="font-G_Bold text-lg">Confirmed</div>
                  <div className="opacity-[0.44] font-G_Regular text-base">
                    We’re confirming your payment details.
                  </div>
                </div>
              </div>
              <img
                src={trailDots}
                alt={"trails"}
                className={`ml-[0.625rem] -mt-2 -mb-2 ${
                  paymentStatus !== "confirmed" &&
                  paymentStatus !== "complete" &&
                  "opacity-[0.43]"
                }`}
              />
            </div>
            <div className="flex flex-col items-start">
              <div
                className={`${
                  paymentStatus == "complete" ? "" : "opacity-[0.43]"
                } flex flex-row gap-4 items-center`}
              >
                <div className="w-6 h-6 rounded-full aspect-square bg-primary"></div>
                <div>
                  <div className="font-G_Bold text-lg">Completed</div>
                  <div className="opacity-[0.44] font-G_Regular text-base">
                    Funds have been added to your account.
                  </div>
                </div>
              </div>
            </div>
            <Button
              className="mt-4 w-full max-w-[15rem]"
              disabled={paymentStatus !== "complete"}
              onClick={(e) => {
                e.preventDefault();
                setOpenFinal(false);
                dispatch(getChallengeRules(getToken, paramId));
              }}
            >
              Done
            </Button>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CryptoChillPopup;

const SocketIo = io(process.env.REACT_APP_BACKEND_URL + "/cryptochill", {
  reconnection: true,
  reconnectionDelay: 100000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: 99999,
});
